import { z } from 'zod';
import { ALERT_FAMILIES, type AlertFamily } from './alert-family';

/**
 * W2-6-6 (D-126): the staff alerts inbox on AlertCase — query, rows and bodies shared by the API, the admin web and
 * (wave 3) the client portal. Rows carry the masked phone only, never the full phone or the customer name (D-87).
 */

export const CASE_STATUSES = ['open', 'in_progress', 'handled', 'not_relevant'] as const;
export type CaseStatusKey = (typeof CASE_STATUSES)[number];

export const CASE_STATUS_HE: Record<CaseStatusKey, string> = { open: 'פתוחה', in_progress: 'בטיפול', handled: 'טופלה', not_relevant: 'לא רלוונטית' };

/** AlertCase.statusVia → how the status was set */
export const CASE_VIA_HE: Record<string, string> = {
  whatsapp_reaction: 'בוואטסאפ',
  whatsapp_reply: 'בתשובה בוואטסאפ',
  backfill: 'מהיסטוריה',
  admin: 'במערכת',
  /** W3-2-1: the client's alerts center (portal) */
  portal: 'במרכז ההתראות',
  /** W2-QA-4: closed by the system (an unsent alert's case) */
  system: 'המערכת',
};

/** W3-2-1: AlertCaseEvent.kind → where a status in the case history came from */
export const CASE_EVENT_SOURCE_HE: Record<string, string> = {
  reaction: 'תגובה בוואטסאפ',
  reply: 'תשובה בוואטסאפ',
  backfill: 'מהיסטוריה',
  admin: 'צוות NIVISION',
  portal: 'מרכז ההתראות',
};

/** W3-2-1: AlertNote.via → where a note came from */
export const NOTE_SOURCE_HE: Record<string, string> = { whatsapp: 'מוואטסאפ', portal: 'ממרכז ההתראות', admin: 'מצוות NIVISION' };

/**
 * W3-2-1: why an alert / case did not go to the group (Alert.reason, AlertCase.notSentReason) — staff wording. Moved from
 * the admin alerts list (apps/web features/B5/alerts-list.tsx) + `nivision_only` (a NIVISION signal with no Alert row).
 */
export const NOT_SENT_REASON_HE: Record<string, string> = {
  backfill: 'שיחה ישנה שעובדה מחדש',
  stale: 'השיחה ישנה מדי',
  alerts_disabled: 'ההתראות כבויות',
  rule_not_enabled: 'הכלל לא נבחר לקבוצה',
  below_min_severity: 'מתחת לחומרה שנבחרה',
  site_unknown: 'הסייט לא מוכר',
  not_sendable: 'הלקוח לא פעיל (טיוטה או מושהה)',
  no_route: 'לא חוברה קבוצה',
  gold_duplicate: 'כבר נשלחה התראת זהב על הלקוח השבוע',
  customer_duplicate: 'כבר נשלחה על הלקוח בימים האחרונים',
  gold_superseded: 'ללקוח יש שיחה מאוחרת שנסגרה / ירדה / בסגירה',
  gold_cap: 'המכסה היומית של התראות זהב לסייט',
  digest_missing: 'נתוני השיחה חסרים',
  outbox_failed: 'השליחה נכשלה',
  outbox_cancelled: 'השליחה בוטלה',
  enqueue_failed: 'לא נכנסה לתור השליחה',
  batch_expired: 'הריכוז לא נשלח בזמן',
  next_brief: 'נדחה לתדריך הבוקר',
  retro: 'התקבל באיחור (השלמת נתונים)',
  nivision_only: 'סימון של NIVISION, לא נשלח',
};

/** W3-2-1: the client-facing wording of a not-sent reason (portal); an unknown reason → PORTAL_NOT_SENT_FALLBACK */
export const PORTAL_NOT_SENT_HE: Record<string, string> = {
  nivision_only: 'סימון של NIVISION, לא נשלח לקבוצה',
  rule_not_enabled: 'ההתראה כבויה אצלכם',
  skipped_rule: 'ההתראה כבויה אצלכם',
  skipped_no_route: 'לא הוגדר נמען להתראה',
  skipped_inactive: 'הלקוח לא היה פעיל',
  failed: 'השליחה נכשלה',
};
export const PORTAL_NOT_SENT_FALLBACK = 'לא נשלח לקבוצה';

/**
 * "Send anyway" (שלח בכל זאת): alert statuses whose skip a person may waive — the call was old, or its rule is not selected.
 * The server (AlertsPipelineService.sendAnyway) and the alerts list in the admin both read these lists.
 */
export const ALERT_SEND_ANYWAY_FROM = ['skipped_stale', 'skipped_rule'] as const;
/**
 * "Send again" (שלח שוב, Orel 25.10): alert statuses whose cause may have been fixed since — the client was activated, a
 * recipient was added, the send failed. The re-run checks client status and routing again (a draft client still gets nothing).
 * A sent / queued alert is never sent again.
 */
export const ALERT_SEND_AGAIN_FROM = ['skipped_inactive', 'skipped_no_route', 'failed'] as const;

/** W3-2-1: the client-facing not-sent line */
export const portalNotSentText = (reason: string | null | undefined): string => (reason && PORTAL_NOT_SENT_HE[reason]) || PORTAL_NOT_SENT_FALLBACK;

export const CASE_SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const;
export type CaseSeverity = (typeof CASE_SEVERITIES)[number];

export interface AlertCaseListQuery {
  scope?: string;
  clientId?: string;
  severity?: CaseSeverity[];
  family?: AlertFamily[];
  /** default 'open' */
  status: CaseStatusKey | 'all';
  /** default 'all' */
  sent: 'all' | 'yes' | 'no';
  /** local YYYY-MM-DD, inclusive */
  from?: string;
  to?: string;
  cursor?: string;
  /** 1..100, default 50 */
  limit: number;
  /** W3-2-1: agent names (AlertCase.agentName); csv in the query string, ≤ 20 names, each 1..60 */
  agent?: string[];
}

const LOCAL_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** a csv string ("CRITICAL,HIGH") or a repeated query param → a non-empty list of allowed values */
const csvOf = <T extends string>(values: readonly [T, ...T[]]) =>
  z.preprocess(
    (v) => {
      const list = Array.isArray(v) ? v.flatMap((x) => String(x).split(',')) : typeof v === 'string' ? v.split(',') : v;
      return Array.isArray(list) ? list.map((s) => s.trim()).filter(Boolean) : list;
    },
    z.array(z.enum(values)).min(1),
  );

/** W3-2-1: a csv string or a repeated param → ≤ 20 trimmed agent names of 1..60 (empty items dropped) */
const agentList = z.preprocess(
  (v) => {
    const list = Array.isArray(v) ? v.flatMap((x) => String(x).split(',')) : typeof v === 'string' ? v.split(',') : v;
    return Array.isArray(list) ? list.map((s) => s.trim()).filter(Boolean) : list;
  },
  z.array(z.string().min(1).max(60)).min(1).max(20),
);

export const AlertCaseListQuerySchema: z.ZodType<AlertCaseListQuery> = z
  .object({
    scope: z.string().min(1).max(64).optional(),
    clientId: z.string().min(1).max(64).optional(),
    severity: csvOf(CASE_SEVERITIES).optional(),
    family: csvOf(ALERT_FAMILIES).optional(),
    status: z.enum([...CASE_STATUSES, 'all']).default('open'),
    sent: z.enum(['all', 'yes', 'no']).default('all'),
    from: z.string().regex(LOCAL_DATE).optional(),
    to: z.string().regex(LOCAL_DATE).optional(),
    cursor: z.string().min(1).max(256).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    agent: agentList.optional(),
  })
  .strict();

/** W3-2-1: the portal's list query — no `scope` (the session decides it), sent to the group by default */
export type PortalCaseListQuery = Omit<AlertCaseListQuery, 'scope' | 'sent'> & { sent: 'all' | 'yes' | 'no' };

/** strict; no `scope`; sent default 'yes'; status default 'open'; limit default 50, 1..100 */
export const PortalCaseListQuerySchema: z.ZodType<PortalCaseListQuery> = z
  .object({
    clientId: z.string().min(1).max(64).optional(),
    severity: csvOf(CASE_SEVERITIES).optional(),
    family: csvOf(ALERT_FAMILIES).optional(),
    status: z.enum([...CASE_STATUSES, 'all']).default('open'),
    sent: z.enum(['all', 'yes', 'no']).default('yes'),
    from: z.string().regex(LOCAL_DATE).optional(),
    to: z.string().regex(LOCAL_DATE).optional(),
    cursor: z.string().min(1).max(256).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    agent: agentList.optional(),
  })
  .strict();

export interface AlertCaseRow {
  id: string;
  origin: 'alert' | 'nivision_signal';
  clientId: string;
  clientName: string;
  scopeParentId: string;
  siteName: string;
  callUuid: string;
  ruleUuid: string;
  ruleName: string;
  family: AlertFamily;
  severity: string;
  occurredAt: string;
  sentAt: string | null;
  sentToGroup: boolean;
  notSentReason: string | null;
  status: CaseStatusKey;
  statusBy: string | null;
  statusVia: string | null;
  statusAt: string | null;
  noteCount: number;
  agentName: string | null;
  phoneMasked: string | null;
  classifierName: string | null;
  outcomeLabel: string | null;
  reportUrl: string | null;
  /** W3-2-1: typed name of whoever handles the case (portal / staff), who set it and when */
  assigneeName: string | null;
  assignedBy: string | null;
  assignedAt: string | null;
  /** W3-2-1: the why / evidence line and the short call link (AlertCase snapshot) */
  whyLine: string | null;
  callLink: string | null;
  /** W3-2-1: a full phone is stored (encrypted) — "הצג מספר" can reveal it */
  hasPhone: boolean;
}

export interface AlertCaseList {
  items: AlertCaseRow[];
  nextCursor: string | null;
  counts: Record<CaseStatusKey | 'all', number>;
  /** W3-2-1: server time of the read (ISO) — the pulse baseline */
  at: string;
}

export interface AlertCaseDetail extends AlertCaseRow {
  notes: { id: string; body: string; authorName: string; via: string; createdAt: string }[];
  events: { kind: string; status: CaseStatusKey; emoji: string | null; actorName: string; at: string; revoked: boolean }[];
}

export const CaseStatusBodySchema: z.ZodType<{ status: CaseStatusKey }> = z.object({ status: z.enum(CASE_STATUSES) }).strict();

/** trimmed 1..1000 */
export const CaseNoteBodySchema: z.ZodType<{ body: string }> = z.object({ body: z.string().trim().min(1).max(1000) }).strict();

/** 1..100 ids */
export const CaseBulkBodySchema: z.ZodType<{ ids: string[]; status: CaseStatusKey }> = z
  .object({ ids: z.array(z.string().min(1).max(64)).min(1).max(100), status: z.enum(CASE_STATUSES) })
  .strict();

/** W3-2-1: control characters and bidi overrides / isolates — never part of a typed name */
// eslint-disable-next-line no-control-regex
const NAME_JUNK = /[\u0000-\u001f\u007f-\u009f\u200e\u200f\u202a-\u202e\u2066-\u2069]/g;

/** W3-2-1: the assignee — a typed name (trimmed, control chars stripped, 2..40) or null to clear it */
export const CaseAssigneeBodySchema: z.ZodType<{ name: string | null }> = z
  .object({
    name: z.preprocess((v) => (typeof v === 'string' ? v.replace(NAME_JUNK, '').trim() : v), z.string().min(2).max(40).nullable()),
  })
  .strict();

/**
 * W3-2-1: why a critical-month line is marked "לבדיקה" — NIVISION alone flagged a cancel / legal family on a first-sales
 * or follow-up call (`CriticalLine.review / reviewReason`, `byFamily[i].review` — W3-2-7).
 */
export type CriticalReviewReason = 'sales_cancel' | 'sales_legal';

export const REVIEW_HE: Record<CriticalReviewReason, string> = {
  sales_cancel: 'סימון ביטול של NIVISION בשיחת מכירה - לעתים קרובות שגוי, כדאי לבדוק',
  sales_legal: 'סימון איום משפטי של NIVISION בשיחת מכירה - כדאי לבדוק',
};

/** W2-6-7: one line of the monthly critical view — one per (call, family) */
export interface CriticalLine {
  callUuid: string;
  occurredAt: string;
  clientId: string;
  clientName: string;
  family: AlertFamily;
  /** distinct rule names, in case order */
  names: string[];
  /** NIVISION signal, our rule (watch included) or both on the same call and family */
  source: 'both' | 'nivision_only' | 'ours_only';
  /** a cancel request on a sales call (first sales / follow-up) — check it is not a misread (report 15 §13 bug 3) */
  salesCheck: boolean;
  /** any case of the line was sent to the group */
  sent: boolean;
  /** the status of the case with the latest statusAt (none → open) */
  status: CaseStatusKey;
  caseIds: string[];
  agentName: string | null;
  /** masked only (D-87) */
  phoneMasked: string | null;
  /** W3-2-7: "לבדיקה" — NIVISION alone flagged a noisy family (cancel / legal) on a first-sales / follow-up call */
  review: boolean;
  /** W3-2-7: why (REVIEW_HE), null without review */
  reviewReason: CriticalReviewReason | null;
}

/** W2-6-7: "קריטיות החודש" — the CRITICAL cases of one local month */
export interface CriticalMonth {
  /** YYYY-MM */
  month: string;
  /** occurredAt desc, then callUuid asc */
  lines: CriticalLine[];
  /** calls desc, then label */
  byFamily: {
    family: AlertFamily;
    label: string;
    calls: number;
    notRelevant: number;
    notRelevantPct: number | null;
    salesCheck: number;
    /** W3-2-7: lines of the family marked "לבדיקה" */
    review: number;
  }[];
  /** calls desc, then name */
  byBranch: { clientId: string; clientName: string; calls: number }[];
}

/** W2-6-7: the inbox scope picker — a parent with its clients, or a client with no parent */
export interface CaseScope {
  id: string;
  name: string;
  kind: 'parent' | 'client';
  children: { id: string; name: string }[];
}

/** portal redesign: the board's period — local days in Asia/Jerusalem ending today (today / the last 7 / the last 30) */
export const CASE_BOARD_PERIODS = ['today', '7d', '30d'] as const;
export type CaseBoardPeriod = (typeof CASE_BOARD_PERIODS)[number];

/** GET /api/p/cases/board — period default '7d'; sent default 'yes' (sent to the group only, as the list) */
export interface PortalCaseBoardQuery {
  period: CaseBoardPeriod;
  sent: 'yes' | 'all';
}

export const PortalCaseBoardQuerySchema: z.ZodType<PortalCaseBoardQuery> = z
  .object({ period: z.enum(CASE_BOARD_PERIODS).default('7d'), sent: z.enum(['yes', 'all']).default('yes') })
  .strict();

/** one board cell: `sent` = cases sent to the group; the four statuses count every case in the cell (they sum to its total) */
export interface CaseBoardCell {
  sent: number;
  open: number;
  inProgress: number;
  handled: number;
  notRelevant: number;
}

/**
 * Portal redesign: the board — the access's branches × the alert families that occurred in the period. A parent access gets
 * one row per branch (by name) + the network totals; a branch / single client access gets its own row and `network` null.
 * Every row and the network carry a cell for every listed family (zeros included). Never a client outside the session.
 */
export interface CaseBoard {
  /** local YYYY-MM-DD, inclusive */
  from: string;
  to: string;
  /** the families with at least one case, in ALERT_FAMILIES order; rules = the distinct rule names inside (Hebrew order) */
  families: { key: AlertFamily; label: string; rules: string[] }[];
  rows: { clientId: string; name: string; total: CaseBoardCell; cells: Partial<Record<AlertFamily, CaseBoardCell>> }[];
  network: { total: CaseBoardCell; cells: Partial<Record<AlertFamily, CaseBoardCell>> } | null;
}
